import React, { useState } from 'react';
import { Button, Modal, message } from 'antd';
import { setAlertDefinitionStatus } from '../functions/alertClauseService';

/**
 * DAS-2871 step 3 — "Alerts that are hurting you".
 *
 * A verdict, not a table.
 *
 * The rest of the Alert Effectiveness modal shows 259 subtypes across 25
 * look-alike percentage columns and leaves the reader to do the statistics.
 * DAS-2869 showed where that leads: sorting by the headline figure produced a
 * screen where every visible row read 100%, 18 of those rested on a single
 * alert, and roughly 27.7 such rows arise from chance alone.
 *
 * So this view deliberately does NOT offer columns, filters or sorting. It
 * answers one question — which alert types are costing money — and it explains
 * its own terms on screen, because a reader should not need a person beside
 * them to interpret it.
 *
 * Three rules it follows:
 *   1. Say what is measured, with a worked example, before showing any number.
 *   2. Never print a bare percentage without the count it came from.
 *   3. Separate "proven" from "suspected", and show the worst-looking figure
 *      that is NOT evidence — that contrast is the entire lesson of DAS-2869.
 */

const RED = '#b42318';
const RED_SOFT = '#c0392b';
const AMBER = '#b26a00';

const box = {
  border: '1px solid #dbe4ee',
  background: '#f5f8fc',
  borderRadius: 10,
  padding: '16px 18px',
  marginBottom: 24,
};

/** One-line description of what a subtype is, for the row headings. */
const subtypeLabel = (r) =>
  r.alert_subtype && r.alert_subtype !== 'Unknown' ? r.alert_subtype : 'combination';

/**
 * Odds in words. The owner reads these figures directly; "1 in 592" is
 * understandable and "p = 0.0017" is not. Anything at or below evens is
 * reported as a coin flip, because that is what it is.
 */
const luckPhrase = (oneIn) => {
  if (!oneIn || oneIn <= 2) return 'a coin flip — proves nothing';
  return `once in ${oneIn} times`;
};

// DAS-2871: the worked example starts COLLAPSED (owner decision) — the tab
// leads with the answer, not the lesson. Opening it is remembered, in
// localStorage like the advisory thresholds (DAS-2356) and clean toggle
// (DAS-2547).
const LS_EXAMPLE_OPEN = 'das2871.hurting.exampleOpen';

/**
 * DAS-2871: the two actions the owner asked for, on every alert shown.
 *
 * "Switch off" is offered ONLY for a single-definition alert type. A
 * combination such as 39 + 46 + 88 is made of definitions that other alerts use
 * too, so switching one off would silently change those as well — a button that
 * does something other than what it says. For combinations we say why and leave
 * only Edit, which opens the definitions so the decision can be made with the
 * clauses in view.
 */
const ActionRow = ({ r, onEdit, onSwitchOff, onShowEffectiveness, busy, mode = 'hurting' }) => {
  const ids = r.definition_ids || [];
  return (
    <div style={{ marginTop: 14, paddingTop: 12, borderTop: '1px solid #eee', display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
      {/* A WORKING alert gets no switch-off offer — there is nothing to act
          on except curiosity, and the button would invite retiring a winner. */}
      {mode !== 'working' && (r.switchable ? (
        <Button danger size="small" loading={busy} onClick={() => onSwitchOff(r)}>
          Switch off
        </Button>
      ) : (
        <span style={{ fontSize: 12.5, color: '#8a6d1f', background: '#fdfaf0', border: '1px solid #f0e2b8', borderRadius: 6, padding: '4px 9px' }}>
          Combination of {ids.length ? ids.join(' + ') : 'several definitions'} — switching one off
          would affect other alerts too, so open it to decide.
        </span>
      ))}
      <Button size="small" onClick={() => onEdit && onEdit(r.identifier)}>
        Edit
      </Button>
      {/* DAS-2871: jump to this alert's rows in the full overview — the
          existing table, prefiltered via its own search box. */}
      <Button size="small" onClick={() => onShowEffectiveness && onShowEffectiveness(r.identifier)}>
        Effectiveness
      </Button>
    </div>
  );
};

const AlertHurtingTab = ({ data, loading, onEdit, onChanged, onShowEffectiveness, mode = 'hurting', benchmark = 'portfolio' }) => {
  const [exampleOpen, setExampleOpen] = useState(() => {
    try {
      return window.localStorage.getItem(LS_EXAMPLE_OPEN) === '1';
    } catch (e) {
      return false;  // collapsed is the default either way
    }
  });
  const toggleExample = () => {
    const next = !exampleOpen;
    setExampleOpen(next);
    try { window.localStorage.setItem(LS_EXAMPLE_OPEN, next ? '1' : '0'); } catch (e) { /* ignore */ }
  };

  const [busyId, setBusyId] = useState(null);
  // The list beyond the top 3 is folded away by default (owner decision).
  const [restOpen, setRestOpen] = useState(false);

  // Switching an alert off is not undoable from this screen, and a combination
  // shares its definitions with other alerts — so confirm, and say plainly what
  // will happen.
  const confirmSwitchOff = (r) => {
    const defId = r.definition_ids && r.definition_ids[0];
    if (!defId) return;
    Modal.confirm({
      title: 'Switch this alert type off?',
      content: (
        <div style={{ fontSize: 13.5, lineHeight: 1.6 }}>
          <strong>{r.alert_subtype}</strong> ({r.alert_type}, definition {defId}) will stop firing
          new alerts. Existing alerts and their history are untouched, and you can switch it back
          on from the alert definitions screen.
        </div>
      ),
      okText: 'Switch off',
      okButtonProps: { danger: true },
      cancelText: 'Cancel',
      onOk: async () => {
        setBusyId(defId);
        try {
          await setAlertDefinitionStatus(defId, 0);
          message.success(`Switched off: ${r.alert_subtype}`);
          if (onChanged) onChanged();
        } catch (e) {
          message.error('Could not switch it off. Nothing was changed.');
        } finally {
          setBusyId(null);
        }
      },
    });
  };

  if (loading) return <div style={{ padding: 24, color: '#888' }}>Loading verdicts…</div>;
  if (!data) {
    return (
      <div style={{ padding: 24, color: '#888' }}>
        Could not load this view. The other tabs are unaffected.
      </div>
    );
  }

  // Owner decision (2026-08-21): no "proven" claim — with 10-80 alerts per
  // type nothing on this screen is proof. The endpoint sends every active
  // alert type ranked by how hard its record is to explain by luck; this
  // component shows one tail of that ranking — the losers ('hurting') or the
  // winners ('working') — top 3 as cards, next 5 folded. One component for
  // both so the two views can never drift apart in wording or method.
  const isWorking = mode === 'working';
  // Two baselines, same machinery. 'portfolio' compares with the owner's whole
  // portfolio (currently contaminated — DAS-2872); 'sp500' compares with just
  // holding SPY, direction-corrected server-side (see DAS-2870 note there).
  const isSP = benchmark === 'sp500';
  const benchLabel = isSP ? 'the S&P 500' : 'your portfolio';
  const pool = (isSP
    ? (isWorking ? data.sp_winners : data.sp_suspects)
    : (isWorking ? data.winners : data.suspects)) || [];
  const top = pool.slice(0, 3);
  const rest = pool.slice(3, 8);
  // The biggest average is often NOT ranked first, because a huge number from
  // a handful of alerts is weaker evidence than a moderate number from many.
  // That deserves an explanation on screen, not silence.
  const extremeByEdge = pool.length
    ? pool.reduce((a, b) => (isWorking
        ? (b.mean_edge > a.mean_edge ? b : a)
        : (b.mean_edge < a.mean_edge ? b : a)))
    : null;
  const weak = extremeByEdge && top[0] && extremeByEdge !== top[0] ? extremeByEdge : null;

  // One palette per tail: red for money lost, green for money made.
  const ACCENT = isWorking ? '#2c7a44' : RED;
  const CARD_BORDER = isWorking ? '#bfe3cb' : '#f0c2bd';
  const CARD_BG = isWorking ? '#f2faf5' : '#fdf4f3';
  const CARD_INNER = isWorking ? '#d9eee0' : '#f0d9d6';
  const notEnough = data.subtypes_not_enough_data || 0;
  const total = data.subtypes_total || 0;
  const months = data.horizon_months || 6;
  const minAlerts = data.min_alerts || 10;

  return (
    <div style={{ maxWidth: 820, color: '#1a1a1a' }}>
      {/* ---- what is measured. Collapsible: essential the first time, clutter
           the tenth. The choice is remembered. ---------------------------- */}
      <div style={box}>
        <div
          onClick={toggleExample}
          style={{
            fontSize: 13, fontWeight: 650, marginBottom: exampleOpen ? 10 : 0,
            cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8,
          }}
        >
          <span style={{ fontSize: 18, lineHeight: 1, color: '#1677ff', fontWeight: 700 }}>
            {exampleOpen ? '▾' : '▸'}
          </span>
          What is measured here
          {!exampleOpen && (
            <span style={{ fontWeight: 400, color: '#777', fontSize: 12.5 }}>
              — better or worse off than {isSP ? 'just holding the S&P 500' : 'ignoring the alert'}, {months} months on
            </span>
          )}
        </div>

        {exampleOpen && (
        <>
        <div style={{ fontSize: 13.5, lineHeight: 1.65, color: '#333' }}>
          We wait until an alert is <strong>{months} months old</strong>. Then we ask one question:
          <span
            style={{
              display: 'inline-block',
              margin: '8px 0',
              padding: '8px 12px',
              background: '#fff',
              borderRadius: 6,
              border: '1px solid #e2e8f0',
            }}
          >
            <em>
              Were you better or worse off following this alert — compared with ignoring it and
              just holding {isSP ? 'the S&P 500 index (SPY)' : 'your portfolio'}?
            </em>
          </span>
        </div>

        <div style={{ fontSize: 13, lineHeight: 1.7, color: '#444', marginTop: 6 }}>
          <strong>Example.</strong> A <em>buy</em> alert fires on a share. Six months later that
          share is up 9%. {isSP ? 'The S&P 500' : 'Your portfolio'} over the same six months is up
          14%. You would have done{' '}
          <strong>better doing nothing</strong> — so this alert counts as{' '}
          <strong style={{ color: RED }}>wrong</strong>, by 5%.
          <br />
          <br />
          Sell alerts are measured the other way round: a sell alert is <strong>right</strong> when
          the share does <em>worse</em> than your portfolio, because getting out was the correct
          call. Monitor alerts simply ask whether the share they flagged beat {benchLabel}.
        </div>

        <div
          style={{
            fontSize: 12.5,
            color: '#666',
            marginTop: 12,
            paddingTop: 10,
            borderTop: '1px solid #dde6f0',
          }}
        >
          Only alerts that have already reached {months} months can be counted, and only alert types
          with at least {minAlerts} of them get a verdict at all. Every verdict also says how many
          different stocks are behind it: when one stock makes up a quarter or more of an alert
          type's alerts, the "could it be luck?" odds are counted per stock instead of per alert,
          because repeated alerts on the same stock are largely one bet. Where enough data exists,
          a "wider test" line shows how the same alert rule did for other accounts on stocks you
          don't own — extra evidence only; it never changes the verdict.
          {data.active_only && ' Switched-off alert types are not shown.'}
        </div>
        </>
        )}
      </div>

      {/* DAS-2872: the portfolio benchmark is under repair (January resets,
           multi-valued days). Until it is fixed these views must say so — a
           silent caveat is how DAS-2869 happened. The vs-S&P views are the
           reliable ones meanwhile. */}
      {!isSP && (
        <div style={{
          border: '1px solid #f0dd9a', background: '#fdf8e7', borderRadius: 8,
          padding: '10px 14px', marginBottom: 16, fontSize: 12.5, color: '#6b5b1e', lineHeight: 1.55,
        }}>
          <strong>Treat this view with care for now.</strong> The portfolio benchmark behind these
          figures has a known fault (DAS-2872) affecting roughly half of the comparisons. The
          vs-S&P-500 views are unaffected and are the reliable ones until it is repaired.
        </div>
      )}

      {/* ---- the ranking. No "proven" — these are suspects, not verdicts. */}
      <div style={{ fontSize: 13, fontWeight: 600, color: ACCENT, marginBottom: 4 }}>
        {isWorking ? 'MOST PROMISING — top 3' : 'MOST SUSPECT — top 3'}
      </div>
      <div style={{ fontSize: 12.5, color: '#666', marginBottom: 10, lineHeight: 1.6, maxWidth: 700 }}>
        The alert types with the {isWorking ? 'best' : 'worst'} record against {benchLabel},
        ranked by how hard that record is to explain by luck — the "once in N times" figure —
        <strong> not by the size of the percentage</strong>. Twelve right out of twelve outranks a
        bigger average built on fewer alerts. With these numbers of alerts nothing is proven —
        these are the strongest {isWorking ? 'candidates' : 'suspects'}, not verdicts.
      </div>

      {pool.length === 0 && (
        <div
          style={{
            border: '1px solid #e8e8e8',
            borderRadius: 10,
            padding: '16px 18px',
            fontSize: 13.5,
            color: '#555',
            marginBottom: 4,
          }}
        >
          No active alert type with enough history is {isWorking ? 'beating your portfolio' : 'losing you money'} right
          now. That is a real answer, not a missing one.
        </div>
      )}

      {top.map((r) => {
        const wrongPct = r.n ? (r.losses / r.n) * 100 : 0;
        return (
          <div
            key={`${r.identifier}-${r.alert_type}`}
            style={{
              border: `1px solid ${CARD_BORDER}`,
              background: CARD_BG,
              borderRadius: 10,
              padding: '16px 18px',
              marginBottom: 10,
            }}
          >
            <div style={{ fontSize: 16, fontWeight: 600 }}>{subtypeLabel(r)}</div>
            <div style={{ fontSize: 13, color: '#777', marginBottom: 14 }}>
              {r.alert_type} · alert type {r.identifier}
            </div>

            <div style={{ display: 'flex', gap: 5, alignItems: 'center' }}>
              <span style={{ fontSize: 12, color: '#666', width: 158 }}>
                {r.n} of these are {months} months old
                {r.n_tickers ? (
                  <span data-testid="das2878-ticker-count" style={{ display: 'block', color: '#888' }}>
                    across {r.n_tickers} {r.n_tickers === 1 ? 'stock' : 'different stocks'}
                  </span>
                ) : null}
                {/* DAS-3054: what this fire count can show at all — not a verdict
                    on the signal (owner decision 2026-08-21: no 'proven' claims). */}
                {r.evidence_text ? (
                  <span data-testid="das3054-evidence" style={{ display: 'block', color: '#8c8c8c', marginTop: 2 }}>
                    {r.evidence_text}
                  </span>
                ) : null}
              </span>
              <div style={{ flex: 1, display: 'flex', height: 24, borderRadius: 4, overflow: 'hidden' }}>
                <div
                  style={{
                    width: `${wrongPct}%`,
                    background: '#d9534f',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#fff',
                    fontSize: 12,
                    fontWeight: 600,
                  }}
                >
                  {r.losses} wrong
                </div>
                <div
                  style={{
                    width: `${100 - wrongPct}%`,
                    background: '#cfe3d4',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#2c5f3f',
                    fontSize: 12,
                  }}
                >
                  {r.wins} right
                </div>
              </div>
            </div>

            <div
              style={{
                marginTop: 16,
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: 14,
              }}
            >
              <div
                style={{
                  background: '#fff',
                  border: `1px solid ${CARD_INNER}`,
                  borderRadius: 8,
                  padding: '12px 14px',
                }}
              >
                <div style={{ fontSize: 24, fontWeight: 650, color: r.mean_edge >= 0 ? '#2c7a44' : RED, lineHeight: 1.1 }}>
                  {r.mean_edge > 0 ? `+${r.mean_edge}` : r.mean_edge}%
                </div>
                <div style={{ fontSize: 12.5, color: '#555', marginTop: 5, lineHeight: 1.5 }}>
                  On average you ended up{' '}
                  <strong>{Math.abs(r.mean_edge)}% {r.mean_edge >= 0 ? 'better' : 'worse'} off</strong> {months} months
                  later than if you had ignored this alert and simply held {benchLabel}.
                </div>
              </div>
              <div
                style={{
                  background: '#fff',
                  border: `1px solid ${CARD_INNER}`,
                  borderRadius: 8,
                  padding: '12px 14px',
                }}
              >
                <div style={{ fontSize: 24, fontWeight: 650, color: '#1a1a1a', lineHeight: 1.1 }}>
                  Could it be luck?
                </div>
                <div style={{ fontSize: 12.5, color: '#555', marginTop: 5, lineHeight: 1.5 }}>
                  If this alert type were really a 50/50 coin flip, a record this{' '}
                  {isWorking ? 'good' : 'bad'} would happen by chance only{' '}
                  <strong>{luckPhrase(r.luck_one_in)}</strong>. Unlikely — but with {r.n} alerts it
                  is suspicion, not proof.
                </div>
                {r.odds_basis === 'tickers' && (
                  <div data-testid="das2878-odds-basis" style={{ fontSize: 12.5, color: '#8a5a00', marginTop: 6, lineHeight: 1.5 }}>
                    These {r.n} alerts come from only {r.n_tickers} different stocks — {r.top_count} of them
                    are {r.top_ticker}. Repeated alerts on one stock is largely one bet, so the odds above
                    count <strong>stocks, not alerts</strong>: {r.ticker_wins} of {r.n_tickers} stocks came out{' '}
                    {isWorking ? 'right' : 'wrong'}.
                  </div>
                )}
                {r.wider_n >= 10 && (
                  <div data-testid="das2855-wider-line" style={{ fontSize: 12.5, color: '#3a5a8c', marginTop: 6, lineHeight: 1.5 }}>
                    <strong>Wider test</strong> — the same alert rule fired for other accounts on stocks
                    you don't own: right <strong>{r.wider_wins} of {r.wider_n}</strong> ({r.wider_hr}%)
                    against the S&P 500. {(r.wider_hr >= 50) === (isWorking) ? 'Agrees with your record.' : 'Disagrees with your record — worth a look.'}
                  </div>
                )}
              </div>
            </div>

            <ActionRow r={r} onEdit={onEdit} onSwitchOff={confirmSwitchOff}
                       onShowEffectiveness={onShowEffectiveness} mode={mode}
                       busy={busyId === (r.definition_ids || [])[0]} />
          </div>
        );
      })}

      {/* ---- the next 5, folded away by default -------------------------- */}
      {rest.length > 0 && (
        <div style={{ marginTop: 18 }}>
          <div
            onClick={() => setRestOpen(!restOpen)}
            style={{ fontSize: 13, fontWeight: 600, color: AMBER, cursor: 'pointer', marginBottom: 6 }}
          >
            {restOpen ? '▾' : '▸'} Next {rest.length} {isWorking ? 'candidates' : 'suspects'}
          </div>

          {restOpen && (
          <div style={{ border: '1px solid #e8e8e8', borderRadius: 10, overflow: 'hidden' }}>
            <div
              style={{
                display: 'flex',
                padding: '9px 16px',
                background: '#fafafa',
                borderBottom: '1px solid #eee',
                fontSize: 11.5,
                color: '#888',
                textTransform: 'uppercase',
                letterSpacing: '.04em',
              }}
            >
              <div style={{ flex: 1 }}>Alert type</div>
              <div style={{ width: 110, textAlign: 'right' }}>Avg per alert</div>
              <div style={{ width: 170, textAlign: 'right' }}>Could this be luck?</div>
              <div style={{ width: 250 }} />
            </div>

            {rest.map((r, i) => (
              <div
                key={`${r.identifier}-${r.alert_type}`}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  padding: '12px 16px',
                  borderBottom: i === rest.length - 1 ? 'none' : '1px solid #f2f2f2',
                }}
              >
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 14 }}>{subtypeLabel(r)}</div>
                  <div style={{ fontSize: 12, color: '#888' }}>
                    {r.alert_type} {r.identifier} · {isWorking ? `right ${r.wins}` : `wrong ${r.losses}`} of {r.n}
                    {r.n_tickers ? ` · ${r.n_tickers} stocks` : ''}
                    {r.odds_basis === 'tickers' ? ' · odds counted per stock' : ''}
                    {r.wider_n >= 10 ? ` · wider test: ${r.wider_wins} of ${r.wider_n} right` : ''}
                  </div>
                </div>
                <div style={{ width: 110, textAlign: 'right', fontSize: 15, fontWeight: 600, color: r.mean_edge >= 0 ? '#2c7a44' : RED_SOFT }}>
                  {r.mean_edge > 0 ? `+${r.mean_edge}` : r.mean_edge}%
                </div>
                <div style={{ width: 170, textAlign: 'right', fontSize: 12.5, color: '#666' }}>
                  {luckPhrase(r.luck_one_in)}
                </div>
                <div style={{ width: 250, textAlign: 'right', display: 'flex', justifyContent: 'flex-end', gap: 6 }}>
                  {!isWorking && (r.switchable ? (
                    <Button size="small" danger loading={busyId === (r.definition_ids || [])[0]}
                            onClick={() => confirmSwitchOff(r)}>
                      Switch off
                    </Button>
                  ) : (
                    <span
                      title="This is a combination of definitions that other alerts use too — switching one off would affect those as well. Open it with Edit to decide."
                      style={{ fontSize: 12, color: '#8a6d1f', alignSelf: 'center' }}
                    >
                      combination
                    </span>
                  ))}
                  <Button size="small" onClick={() => onEdit && onEdit(r.identifier)}>Edit</Button>
                  <Button size="small" onClick={() => onShowEffectiveness && onShowEffectiveness(r.identifier)}>Effectiveness</Button>
                </div>
              </div>
            ))}
          </div>
          )}
        </div>
      )}

      {/* ---- the teaching case: worst number, weakest evidence ----------- */}
      {weak && (
        <div
          style={{
            marginTop: 20,
            borderLeft: '3px solid #d0a215',
            background: '#fdfaf0',
            padding: '14px 16px',
            borderRadius: '0 8px 8px 0',
          }}
        >
          <div style={{ fontSize: 13, color: '#4a4a4a', lineHeight: 1.6 }}>
            <strong>Why the {isWorking ? 'best' : 'worst'} number is not at the top.</strong>
            <br />
            “{subtypeLabel(weak)}” has the {isWorking ? 'best' : 'worst'} average on this tab
            (<strong>{isWorking ? `+${weak.mean_edge}` : weak.mean_edge}%</strong>) but only{' '}
            <strong>{weak.n}</strong> results behind it. A big number from a handful of alerts is
            weaker evidence than a moderate number from many, so it ranks lower.
          </div>
        </div>
      )}

      {/* ---- what cannot be judged --------------------------------------- */}
      <div
        style={{
          marginTop: 18,
          padding: '14px 16px',
          background: '#f6f6f6',
          borderRadius: 8,
          fontSize: 13,
          color: '#555',
          lineHeight: 1.6,
        }}
      >
        <strong style={{ color: '#1a1a1a' }}>
          {notEnough} of your {total} alert types are not on this tab at all.
        </strong>{' '}
        Fewer than {minAlerts} of their alerts have reached {months} months, so there is nothing yet
        to measure. They are not hidden or filtered out — there is simply no answer to give.
      </div>
    </div>
  );
};

export default AlertHurtingTab;
